import { useCallback, useEffect, useState } from "react";
import TableButton from "components/atoms/buttons/TableButton";
import { NoticePresenter } from "./NoticePresenter";
import * as s from "./NoticeStyled";
import ToggleComponent from "components/atoms/toggle/Toggle";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getNotices, putDisplayNotice } from "api/settingAPI";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useDispatch } from "react-redux";
import {
  deleteNotice,
  updateNotice,
} from "redux/reducers/notices/noticeReducer";
import { NoticeInfo } from "api/interfaces/noticeInterface.interface";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { isNull } from "lodash";
import { TableColumnsType } from "antd";
import { ColumnType } from "antd/es/table";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { extractTextFromHTML } from "utils/functions";
import { useMenu } from "hook/useMenu";
import { useQueryKey } from "utils/queryUtil";
import * as MediaQuery from "components/MediaQuery";
import useApiError from "hook/useApiError";

const initialQueryInfo = {
  total: 0,
  pageNum: 0,
  pageLimit: 20,
  keyword: "",
  sortType: "subject",
  sortDirection: SORT_DIRECTION.ASC,
};
export default function EmailContainer(): JSX.Element {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const location: Location = useLocation();
  const navigate = useNavigate();
  const isMobile = MediaQuery.useIsMobile();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const notices: NoticeInfo[] = useAppSelector((state) => state.notice.notices);
  const [isAddModal, setIsAddModal] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);

  const [pending, setPending] = useState<boolean>(true);

  const [selectedNotice, setSelectedNotice] = useState<
    NoticeInfo | undefined
  >();

  const [status, setStatus] = useState({
    totalTitle: "Total",
    totalValue: 0,
  });

  const [paginationInfo, setPaginationInfo] = useState<BasePagination>({
    ...initialQueryInfo,
  });
  const [noticeList, setNoticeList] = useState<NoticeInfo[]>([]);

  const { queryNavigate } = useMenu({});
  const { lang, notifyError } = useApiError();

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setPaginationInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "subject"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.DESC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setPaginationInfo((info) => {
        return {
          ...info,
          pageNum: 0,
          pageLimit: 20,
          keyword: "",
          sortType: "subject",
          sortDirection: SORT_DIRECTION.DESC,
        };
      });
    }
  }, [location]);

  const queryKey = useQueryKey(paginationInfo);

  const { refetch } = useQuery(
    ["getNoticeInNoticePage", queryKey],
    () =>
      getNotices({
        payload: paginationInfo,
      }),

    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        // console.log(res, "res Notice!!");
        if (res.error !== 0) {
          setNoticeList([]);
          setStatus((info) => {
            return {
              ...info,
              totalValue: 0,
            };
          });
          setPaginationInfo({ ...initialQueryInfo });
          return;
        }
        setNoticeList(res.result);
        setStatus((info) => {
          return {
            ...info,
            totalValue: res.page.total,
          };
        });
        setPaginationInfo((info) => {
          return { ...info, ...res.page };
        });
      },
      onError: (e: any) => {
        setNoticeList([]);
        setStatus((info) => {
          return {
            ...info,
            totalValue: 0,
          };
        });
        setPaginationInfo({ ...initialQueryInfo });
      },
      onSettled: (e: any) => {
        setPending(false);
      },
    }
  );

  const onHeaderCell = (column: ColumnType<NoticeInfo>) => {
    return {
      onClick: () => {
        if (column.key) {
          setPaginationInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  const columns: TableColumnsType<NoticeInfo> = [
    {
      title: "Title",
      sorter: true,
      sortOrder: changeSortOrderAntd(paginationInfo, "subject"),
      onHeaderCell: onHeaderCell,
      defaultSortOrder: "ascend",
      key: "subject",
      dataIndex: "subject",
      render: (value, row) => (
        <div>
          <s.Text>{row.subject}</s.Text>
          <MediaQuery.Default>
            <s.Text isDescription>{extractTextFromHTML(row.message)}</s.Text>
          </MediaQuery.Default>
        </div>
      ),

      // ellipsis: true,
    },
    {
      title: "Color",
      render: (value, row) => <s.Color color={row.color} />,
      width: isMobile ? 50 : 70,
      align: "center",
      responsive: ["md"],
    },
    {
      title: "Display",
      width: isMobile ? 80 : 100,
      render: (value, row) => (
        <ToggleComponent
          isToggled={row.isDisplay}
          onChange={(
            checked: boolean,
            event: React.MouseEvent<HTMLButtonElement>
          ) => {
            event.stopPropagation();
            if (row.noticeId) {
              onUpdateDisplay(checked, row.noticeId);
            }
          }}
          dataTestId="manager-site-notices-table-button-toggle"
        />
      ),

      align: "center",
      responsive: ["md"],
    },
    // 마지막 버튼
    {
      title: "",
      width: isMobile ? 120 : 180,
      render: (value, row, index) => (
        <s.ButtonWrapper>
          <TableButton
            label="Edit"
            onClick={(e) => {
              e.stopPropagation();

              onModal("edit");
              setSelectedNotice(row);
            }}
            data-testid={`manager-site-notices-table-button-edit-${index}`}
          />

          <span className="row-click-button">
            <TableButton
              label="Preview"
              onClick={(e) => {
                e.stopPropagation();
                onRowClicked(row);
              }}
              data-testid={`manager-site-notices-table-button-preview-${index}`}
            />
          </span>
        </s.ButtonWrapper>
      ),

      align: "right",
    },
  ];

  const onRowClicked = useCallback(
    (row: NoticeInfo) => {
      if (notices.find((not) => not.noticeId === row.noticeId)) {
        return;
      }
      dispatch(updateNotice({ ...row, preview: true }));
      setTimeout(() => {
        dispatch(deleteNotice(row.noticeId));
      }, 3000);
    },
    [notices]
  );

  const onModal = (type?: string) => {
    if (type === "add") {
      setIsAddModal(!isAddModal);
    }
    if (type === "edit") {
      setIsEditModal(!isEditModal);
    }
  };

  const mutationUpdateDisplayNotice = useMutation(putDisplayNotice, {
    onSuccess: () => {
      // console.log("success");
      queryClient.invalidateQueries("getNoticeInNoticePage");
      setIsEditModal(false);
    },
    onError: () => {
      // console.log("fail");
      notifyError(lang.label.setting.notice.notify.display.update.fail);
    },
  });

  const onUpdateDisplay = useCallback(
    (isDisplay: boolean, noticeId: string) => {
      mutationUpdateDisplayNotice.mutate({
        isDisplay: isDisplay,
        noticeId: noticeId,
        accountId: selectedAccount.accountId,
      });
    },
    [mutationUpdateDisplayNotice, noticeList, selectedAccount]
  );

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (paginationInfo.keyword !== keyword) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: paginationInfo.pageLimit,
            pageNum: 0,
            keyword: keyword,
            sortType: paginationInfo.sortType,
            sortDirection: paginationInfo.sortDirection as SORT_DIRECTION,
          },
        });
      } else {
        refetch();
      }
      setPending(true);
      // setPaginationInfo((query) => {
      //   return {
      //     ...query,
      //     keyword,
      //   };
      // });
      // refetch();
    },
    [
      location.pathname,
      paginationInfo.keyword,
      paginationInfo.pageLimit,
      paginationInfo.sortDirection,
      paginationInfo.sortType,
      refetch,
      queryNavigate,
    ]
  );

  const onChangePage = (page: number, totalRows: number) => {
    // console.log(page, totalRows);
    if (page !== paginationInfo.pageNum + 1) {
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          pageLimit: paginationInfo.pageLimit,
          pageNum: page - 1,
          keyword: paginationInfo.keyword as string,
          sortType: paginationInfo.sortType,
          sortDirection: paginationInfo.sortDirection as SORT_DIRECTION,
        },
      });
      setPending(true);
      // setPaginationInfo((info) => {
      //   return { ...info, pageNum: (page - 1) as number };
      // });
    }
  };

  return (
    <NoticePresenter
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      columns={columns}
      onClickSearch={onClickSearch}
      onRowClicked={onRowClicked}
      data={noticeList}
      selectedNotice={selectedNotice}
      onChangePage={onChangePage}
      paginationInfo={paginationInfo}
      status={status}
      tablePending={pending}
    />
  );
}
